import { useCallback, useEffect, useState } from "react";
import { execService } from "@/services";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Receipt, Loader2, Lock } from "lucide-react";
import { useTenantScope, TenantPicker, SelectTenantNotice, PageHeader } from "./shared";

export default function CollectionsPlaceholder() {
  const scope = useTenantScope();
  const { isSuper, tenantId, params } = scope;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const blocked = isSuper && !tenantId;

  const load = useCallback(() => {
    if (blocked) { setData(null); return; }
    setLoading(true);
    execService.collections(params).then(setData).catch((e) => toast.error(apiErrorMessage(e.response?.data?.detail))).finally(() => setLoading(false));
  }, [blocked, params]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="exec-collections-page">
      <PageHeader icon={Receipt} title="Collections Dashboard" subtitle="Accounts receivable & collections analytics.">
        <TenantPicker {...scope} />
      </PageHeader>

      {blocked ? <SelectTenantNotice /> : loading && !data ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : data ? (
        <div className="bg-card border border-border rounded-xl p-8" data-testid="collections-placeholder">
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border border-amber-500/30 bg-amber-500/10 text-amber-600"><Lock className="h-3.5 w-3.5" /> Coming soon</span>
          </div>
          <h2 className="text-lg font-bold text-foreground">{data.title}</h2>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl leading-relaxed">{data.message}</p>

          <div className="grid sm:grid-cols-2 gap-6 mt-6">
            <div>
              <h3 className="text-xs font-bold uppercase text-muted-foreground mb-2">Planned KPIs</h3>
              <ul className="space-y-1.5">
                {(data.plannedKpis || []).map((k) => <li key={k} className="text-sm text-foreground flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-primary" /> {k}</li>)}
              </ul>
            </div>
            <div>
              <h3 className="text-xs font-bold uppercase text-muted-foreground mb-2">Canonical sources (planned)</h3>
              <div className="flex flex-wrap gap-2">
                {(data.plannedSources || []).map((s) => <span key={s} className="text-xs text-muted-foreground bg-muted/40 border border-border rounded px-2 py-1">{s}</span>)}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
